/**
 * Project Data — Layer 4 of the AI Router.
 *
 * Static, descriptive metadata about smartcalcAi's own feature set —
 * distinct from knowledgeBase.js (which answers "what/who is this app")
 * and from the calculator engine (which does actual math). This layer
 * only runs AFTER the calculator engine has already tried and returned
 * 'unknown' for a query, so it can never shadow a real calculation.
 *
 * Purely additive — new file, does not touch aiEngine.js.
 */

const FEATURE_LIST = [
  "Percentage, GST, discount, EMI, compound/simple interest, SIP, FD, RD",
  "BMI, ideal weight, water intake, daily calories",
  "Profit/loss, margin, markup, CAGR, ROI, depreciation, CGPA, attendance %",
  "Ohm's law, geometry, LCM/GCD, quadratic equations, statistics, permutations/combinations, probability",
  "Unit conversion, number base conversion, scientific notation, inflation, live currency conversion, salary/CTC breakdown",
  "Date difference, age calculator, date math, Fibonacci, general math expressions",
];

const FORMULA_NOTES = {
  emi: "EMI is calculated as P × r × (1+r)^n / ((1+r)^n − 1), where P is principal, r is the monthly interest rate, and n is the number of months. Try: \"emi for 5 lakh at 8.5% for 5 years\".",
  sip: "SIP (Systematic Investment Plan) future value uses the formula P × [((1+i)^n − 1) / i] × (1+i), where P is the monthly investment, i is the periodic rate, and n is the number of installments. Try: \"sip of 5000 per month at 12% for 10 years\".",
  bmi: "BMI is weight (kg) divided by height (m) squared. Try: \"bmi 70kg 170cm\".",
  cagr: "CAGR is (Ending Value / Beginning Value)^(1/years) − 1. Try: \"cagr from 10000 to 25000 in 5 years\".",
  gst: "GST is calculated as amount × (GST rate / 100), added to or already included in the base amount depending on how you phrase it.",
};

const ENTRIES = [
  {
    pattern: /\bwhat can you (calculate|do)\b|\blist.*(calculators|features)\b|\bwhat (calculators|features) (do you have|are there)\b/i,
    answer: () =>
      "Here's what I can calculate:\n\n" + FEATURE_LIST.map((f) => `• ${f}`).join("\n"),
    suggestions: ["15% of 2500", "emi for 5 lakh at 8.5% for 5 years", "bmi 70kg 170cm"],
  },
  {
    pattern: /\bhow does (emi|sip|bmi|cagr|gst) work\b|\b(emi|sip|bmi|cagr|gst) formula\b/i,
    answer: (rawQuery) => {
      const key = Object.keys(FORMULA_NOTES).find((k) =>
        new RegExp(`\\b${k}\\b`, "i").test(rawQuery)
      );
      return key ? FORMULA_NOTES[key] : null;
    },
  },
];

/**
 * Returns { answer, suggestions? } on a match, or null.
 */
export function lookupProjectData(rawQuery) {
  const q = String(rawQuery || "").trim();
  if (!q) return null;
  for (const entry of ENTRIES) {
    if (entry.pattern.test(q)) {
      const answer = typeof entry.answer === "function" ? entry.answer(q) : entry.answer;
      if (answer) return { answer, suggestions: entry.suggestions || [] };
    }
  }
  return null;
}

export default ENTRIES;
